import { IGetPlanSeriesMapArgs, productsManager } from '@/lib';
import type { IPlanCardItem, IPlanDisplayMapperResult } from './types';
import { EPlanStatus } from '@reolink-cloud/interface';

const CYCLE_ORDER: Record<string, number> = {
    day: 1,
    week: 2,
    month: 3,
    year: 0
};

function compareCycleKey ( a: string, b: string ): number {

    const [aAmount, aUnit] = a.split( '-' );
    const [bAmount, bUnit] = b.split( '-' );
    const aNum = parseInt( aAmount, 10 );
    const bNum = parseInt( bAmount, 10 );
    const unitOrder = ( CYCLE_ORDER[aUnit] ?? 99 ) - ( CYCLE_ORDER[bUnit] ?? 99 );
    if ( unitOrder !== 0 ) return unitOrder;
    return aNum - bNum;

}

/**
 * 将 getPlanSeriesMap 转为按周期分组的扁平卡片列表与周期列表。
 * 排序：series.sort 最优先，再周期，周期内 plan 已按 plan.sort 降序。
 * 仅在 PlanCycleCardGroup 内部调用，父组件不调用。
 */
export function planDisplayMapper ( args: IGetPlanSeriesMapArgs ): IPlanDisplayMapperResult {

    const planSeriesMap = productsManager.getPlanSeriesMap( args );

    const seriesKeys = Object.keys( planSeriesMap );
    const sortedSeriesKeys = seriesKeys
        .map( key => ({ key, sort: productsManager.getPlanSeriesByKey( key ).style['series.sort'] ?? 999 }))
        .sort(( a, b ) => a.sort - b.sort )
        .map( item => item.key );

    const cardList: IPlanCardItem[] = [];
    const cycleKeySet = new Set<string>();

    for ( const seriesKey of sortedSeriesKeys ) {

        const seriesInfo = productsManager.getPlanSeriesByKey( seriesKey );
        const isRecommended = !!seriesInfo.style['series.recommended'];
        const strategyMap = planSeriesMap[seriesKey];
        if ( !strategyMap ) continue;

        const cycleKeysPerSeries = new Set<string>();
        for ( const strategyKey of Object.keys( strategyMap )) {

            const cycleMap = strategyMap[strategyKey];
            for ( const ck of Object.keys( cycleMap )) cycleKeysPerSeries.add( ck );

        }
        const sortedCycleKeys = Array.from( cycleKeysPerSeries ).sort( compareCycleKey );

        for ( const cycleKey of sortedCycleKeys ) {

            for ( const strategyKey of Object.keys( strategyMap )) {

                const plans = strategyMap[strategyKey][cycleKey];
                if ( !plans?.length ) continue;
                const [amount, unit] = cycleKey.split( '-' );
                const cycleLabel = `${amount}-${unit}`;
                for ( const plan of plans ) {

                    cardList.push({
                        plan,
                        cycleKey,
                        cycleLabel,
                        isRecommended: isRecommended && plan.status ===  EPlanStatus.ENABLED
                    });
                    cycleKeySet.add( cycleKey );

                }

            }

        }

    }

    const durationKeys = Array.from( cycleKeySet ).sort( compareCycleKey );
    const durationList = durationKeys.map( key => {

        const [amountStr, unit] = key.split( '-' );
        return { unit, amount: parseInt( amountStr, 10 ) };

    });

    /** 多个 series 在同周期都设推荐时，每个周期各保留遍历顺序中最靠前的一条 */
    const cycleKeptRecommended = new Set<string>();
    for ( const item of cardList ) {

        if ( !item.isRecommended ) continue;
        if ( cycleKeptRecommended.has( item.cycleKey )) item.isRecommended = false;
        else cycleKeptRecommended.add( item.cycleKey );

    }

    return { cardList, durationList };

}
